import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Search, MapPin, X, User as UserIcon, Loader2 } from "lucide-react";
import { GoogleMap, useJsApiLoader, Marker as GMarker, Autocomplete } from "@react-google-maps/api";
import { API } from "../App";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const GOOGLE_LIBRARIES = ["places"];

/* ============================================================
 * SegmentAdminPicker
 * Search + select a customer to elevate to Segment Admin.
 * ========================================================== */
export function SegmentAdminPicker({ value, label, onChange }) {
  const [q, setQ] = useState(label || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setQ(label || ""); }, [label]);

  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await fetch(`${API}/admin/users/eligible-segment-admins?q=${encodeURIComponent(q)}`, { credentials: "include" });
        if (r.ok) setResults(await r.json());
      } catch { /* silent */ }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(handle);
  }, [q, open]);

  const pick = (u) => {
    const lbl = u.email || u.name || u.user_id;
    onChange(u.user_id, lbl);
    setQ(lbl);
    setOpen(false);
  };

  const clear = () => { onChange("", ""); setQ(""); };

  return (
    <div className="space-y-1.5 relative" data-testid="segment-admin-picker">
      <Label className="flex items-center gap-1.5 text-sm">
        <UserIcon size={13} className="text-gray-500" /> Segment Admin (optional)
      </Label>
      <div className="relative">
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search a customer by email or name…"
          data-testid="segment-admin-search"
        />
        {value && (
          <button type="button" onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500" title="Clear">
            <X size={14} />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {busy ? (
            <div className="px-3 py-2 text-xs text-gray-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">No eligible customers</div>
          ) : results.map((u) => (
            <button
              type="button"
              key={u.user_id}
              onClick={() => pick(u)}
              className="w-full text-left px-3 py-2 hover:bg-green-50 flex items-center justify-between gap-2"
              data-testid={`segment-admin-option-${u.user_id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{u.name || "Unnamed"}</p>
                <p className="text-[11px] text-gray-500 truncate">{u.email}</p>
              </div>
              {u.role === "segment_admin" && <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">SA</span>}
            </button>
          ))}
        </div>
      )}
      <p className="text-[10px] text-gray-400">When selected, this customer is automatically promoted to <b>Segment Admin</b> for this segment.</p>
    </div>
  );
}


/* ============================================================
 * SegmentAddressInput
 * Default shipping address with Google Maps autocomplete + map
 * ========================================================== */
const EMPTY_ADDR = { address_line: "", landmark: "", city: "", state: "", pincode: "", lat: 12.9716, lng: 77.5946 };

export function SegmentAddressInput({ value, onChange }) {
  const [form, setForm] = useState(value || EMPTY_ADDR);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_KEY, libraries: GOOGLE_LIBRARIES });

  useEffect(() => { setForm(value || EMPTY_ADDR); }, [value]);

  const update = (next) => {
    setForm(next);
    // Bubble up null when address is cleared
    const isEmpty = !next.address_line?.trim() && !next.city?.trim();
    onChange(isEmpty ? null : next);
  };

  const onPlaceSelected = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const components = place.address_components || [];
    let city = "", state = "", pincode = "";
    for (const c of components) {
      if (c.types.includes("locality")) city = c.long_name;
      if (c.types.includes("administrative_area_level_1")) state = c.long_name;
      if (c.types.includes("postal_code")) pincode = c.long_name;
      if (!city && c.types.includes("sublocality_level_1")) city = c.long_name;
    }
    update({
      ...form, lat, lng,
      address_line: place.formatted_address?.split(",").slice(0, 3).join(",").trim() || place.name || form.address_line,
      city: city || form.city, state: state || form.state, pincode: pincode || form.pincode,
    });
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(16);
  };

  return (
    <div className="space-y-2" data-testid="segment-address-input">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5 text-sm">
          <MapPin size={13} className="text-gray-500" /> Default Shipping Address
        </Label>
        {(form.address_line || form.city) && (
          <button type="button" onClick={() => update(EMPTY_ADDR)} className="text-[10px] text-red-500 hover:text-red-600 font-semibold" data-testid="segment-address-clear">
            Clear
          </button>
        )}
      </div>

      {isLoaded ? (
        <Autocomplete
          onLoad={(auto) => (autocompleteRef.current = auto)}
          onPlaceChanged={onPlaceSelected}
          options={{ componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] }}
        >
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search address (e.g. TCS Whitefield Bangalore)…" className="pl-9 h-9 text-sm" data-testid="segment-address-search" />
          </div>
        </Autocomplete>
      ) : (
        <div className="text-[11px] text-gray-400">Maps loading…</div>
      )}

      {isLoaded && (
        <div className="h-32 rounded-lg overflow-hidden border border-gray-200">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={{ lat: form.lat || 12.9716, lng: form.lng || 77.5946 }}
            zoom={form.lat ? 15 : 5}
            onLoad={(map) => (mapRef.current = map)}
            options={{ disableDefaultUI: true, zoomControl: true }}
          >
            {form.lat && <GMarker position={{ lat: form.lat, lng: form.lng }} />}
          </GoogleMap>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Input
          value={form.address_line}
          onChange={(e) => update({ ...form, address_line: e.target.value })}
          placeholder="Address line"
          className="h-9 text-sm col-span-2"
          data-testid="segment-address-line"
        />
        <Input
          value={form.landmark}
          onChange={(e) => update({ ...form, landmark: e.target.value })}
          placeholder="Landmark (optional)"
          className="h-9 text-sm col-span-2"
        />
        <Input
          value={form.city}
          onChange={(e) => update({ ...form, city: e.target.value })}
          placeholder="City"
          className="h-9 text-sm"
          data-testid="segment-address-city"
        />
        <Input
          value={form.state}
          onChange={(e) => update({ ...form, state: e.target.value })}
          placeholder="State"
          className="h-9 text-sm"
        />
        <Input
          value={form.pincode}
          onChange={(e) => update({ ...form, pincode: e.target.value })}
          placeholder="Pincode"
          className="h-9 text-sm col-span-2"
        />
      </div>
      <p className="text-[10px] text-gray-400">Members of this segment ordering from a kitchen tagged to this segment will have their delivery address forced to this location at checkout.</p>
    </div>
  );
}
